import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  DateField,
  BooleanField,
  EditButton,
  Filter,
  TextInput,
  ReferenceInput,
  SelectInput,
  FunctionField
} from 'react-admin';
import { Chip } from '@mui/material';
import { AcademicClassGroupInput } from './classGroups';

const AcademicContentFilter = (props: any) => (
  <Filter {...props} variant="outlined">
    <TextInput source="title" label="Search title" alwaysOn />
    <ReferenceInput source="exam_id" reference="exam" alwaysOn>
      <SelectInput optionText="exam_name" />
    </ReferenceInput>
    <AcademicClassGroupInput source="class_group_id" alwaysOn />
    <ReferenceInput source="subject_id" reference="subject" alwaysOn>
      <SelectInput optionText="subject" />
    </ReferenceInput>
    <ReferenceInput source="content_type_id" reference="content-type" perPage={50} alwaysOn>
      <SelectInput optionText="content_type" label="Content type" />
    </ReferenceInput>
    {/* Staff can see unpublished rows; the API forces is_active=1 for everyone
        else regardless of what this sends. */}
    <SelectInput
      source="is_active"
      label="Status"
      choices={[
        { id: '1', name: 'Published' },
        { id: '0', name: 'Unpublished' }
      ]}
      alwaysOn
    />
  </Filter>
);

/** Bytes as something an admin can read at a glance. */
const formatSize = (bytes: any) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

export const AcademicContentList = (props: any) => (
  <List
    {...props}
    title="Academic Content"
    filters={<AcademicContentFilter />}
    // Newest first: the working pattern here is upload a batch, then check what
    // just landed.
    sort={{ field: 'created_at', order: 'DESC' }}
    perPage={25}
  >
    <Datagrid rowClick="edit" bulkActionButtons={false}>
      {/* The card preview, at the size it is actually useful. A fixed box with
          object-fit keeps the row heights even whatever shape was uploaded. */}
      <FunctionField
        label=""
        render={(record: any) =>
          record.thumbnail_url ? (
            <img
              src={record.thumbnail_url}
              alt=""
              style={{ width: 56, height: 40, objectFit: 'cover', borderRadius: 3, display: 'block' }}
            />
          ) : (
            <span style={{ opacity: 0.4 }}>—</span>
          )
        }
      />

      <TextField source="title" label="Title" />
      <TextField source="exam_name" label="Exam" />
      <TextField source="class_group" label="Class" />
      <TextField source="subject" label="Subject" />

      {/* The joined chapter name, or an explicit marker. A blank cell here
          would read as missing data rather than as deliberate. */}
      <FunctionField
        label="Chapter"
        render={(record: any) =>
          record.chapter_id ? record.chapter_name : <span style={{ opacity: 0.5 }}>Whole subject</span>
        }
      />

      <FunctionField
        label="Type"
        render={(record: any) => (
          <Chip
            size="small"
            label={record.content_type}
            color={record.media === 'video' ? 'secondary' : 'default'}
          />
        )}
      />

      {/* Says what the file actually is, not what the content type implies.
          Since migration 0011 one type can hold both PDFs and images, so
          reading "PDF" off the type would be wrong for half the rows. */}
      <FunctionField
        label="File"
        render={(record: any) => {
          const url = record.media === 'video' ? record.video_url : record.file_url;
          if (!url) return '—';

          let label;
          if (record.media === 'video') {
            label = 'Watch';
          } else {
            const kind = record.file_type === 'image' ? 'Image' : 'PDF';
            label = `${kind} · ${formatSize(record.file_size_bytes)}`;
          }

          return (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              {label}
            </a>
          );
        }}
      />

      <BooleanField source="is_active" label="Published" />
      <DateField source="created_at" label="Added" />
      <EditButton />
    </Datagrid>
  </List>
);
